<script setup lang="ts">
import Button from '~~/components/design-system/button.vue';
import { CheckCircleIcon } from '@heroicons/vue/24/outline';
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import GenericError from '~/components/user-interface/generic-error.vue';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';
import { ESumSubAction } from '~/interfaces/dto/compliance/request';

const formLoading = ref<boolean>(true);
const { $authService } = useServices();
const { $t, $trackEvent, $enabled } = useNuxtApp();
const auth = useAuthStore();
const site = useSiteStore();
const errorCode = ref<string>('');
const errorRef = ref<string>('');

const form = ref<IFormBuilderReference>();

const model = ref({
  password: '',
  passwordConfirmation: '',
});

const sumSubEnabled = computed(() =>
  $enabled('security.highriskeventsprofilemanagement'),
);

const showSumSub = ref<boolean>(false);

const navigateToSuccess = () => {
  // navigate to reset success
  auth.toggleAccountModal(false);
  site.activateModal('passwordResetStepSuccess');
};

const handleChangePassword = () => {
  if (sumSubEnabled.value) showSumSub.value = true;
  else confirmPasswordChange();
};

// Submit new password
const confirmPasswordChange = async () => {
  $trackEvent('Account', 'MY_ACCOUNT_PASSWORD_UPDATE_ACTION');
  if (
    !model.value.password ||
    !model.value.passwordConfirmation ||
    model.value.password !== model.value.passwordConfirmation
  )
    return;
  errorCode.value = null;

  const data = {
    confirmPassword: model.value.passwordConfirmation,
    password: model.value.password,
  };
  formLoading.value = true;

  try {
    const response = await $authService.passwordResetLoggedIn(data);
    if (response?.isSuccessful) {
      $trackEvent('Account', 'MY_ACCOUNT_PASSWORD_UPDATE_SUCCESS');
      navigateToSuccess();
    } else {
      errorCode.value = String(response?.error?.displayCode ?? 'general');
      errorRef.value = String(response?.error?.code ?? '');
      console.error(response, errorCode.value);
      $trackEvent('Account', 'MY_ACCOUNT_PASSWORD_UPDATE_FAILURE', {
        message: errorCode.value || response?.cause?.message,
      });
    }
  } catch (error) {
    console.error(error);
    errorCode.value = 'general';
    errorRef.value = '';
  } finally {
    formLoading.value = false;
    showSumSub.value = false;
  }
};

const handleError = (event: string) => (errorCode.value = event);

onBeforeMount(() => (formLoading.value = true));
onMounted(() => {
  $trackEvent('Account', 'MY_ACCOUNT_SETTINGS', {
    to: 'update password',
  });
});
</script>

<template>
  <div
    class="mx-auto sm:max-w-[428px] rounded-none flex flex-col justify-center items-center gap-2 relative pb-2 border-t border-b stacked-border-color"
    :class="{
      'aspect-video': !form?.loaded,
    }"
    @keyup.enter="handleChangePassword"
  >
    <div class="w-full">
      <UserInterfaceDynamicFormBuilder
        ref="form"
        :show-sub-title="true"
        :sub-title-classes="[{ 'text-sm font-normal my-2': !form?.isEmpty }]"
        show-title
        :title-classes="[
          { 'font-bold text-base text-base-priority mt-0': !form?.isEmpty },
        ]"
        :container-classes="[{ 'p-4': !form?.isEmpty }]"
        template-name="update password"
        :model-value="model"
        @error="handleError($event)"
        @loaded="formLoading = false"
      >
        <template v-if="!form?.isEmpty" #form-actions="formActionsProps">
          <div class="flex flex-col gap-3">
            <div
              v-if="
                formActionsProps?.form?.validated &&
                !formActionsProps?.form?.invalid
              "
            >
              <div class="flex items-center gap-2 px-4">
                <CheckCircleIcon
                  class="dark:text-primary-blue-500 text-primary-blue-400 icon-success--border w-5"
                />
                <span class="text-xs font-light text-base-priority">
                  {{ $t('Passwords-must-match-and-be-valid') }}
                </span>
              </div>
            </div>

            <div v-if="errorCode" class="flex justify-center px-4">
              <GenericError
                class="max-w-72 mx-auto text-sm"
                state="danger"
                :reference-code="errorRef"
              >
                {{ $t(`jpc-update-password-error-${errorCode}`) }}
              </GenericError>
            </div>
            <div
              class="text-sm flex flex-row justify-center px-4 pb-2 rounded-b-lg"
            >
              <Button
                class="uppercase w-full justify-center text-sm"
                rounding="lg"
                :disabled="
                  !(
                    formActionsProps?.form?.validated &&
                    !formActionsProps?.form?.invalid &&
                    model.password === model.passwordConfirmation
                  )
                "
                @click="handleChangePassword"
              >
                {{ $t('update-password') }}
              </Button>
            </div>
          </div>
        </template>
      </UserInterfaceDynamicFormBuilder>
      <div v-if="errorCode && form?.isEmpty" class="flex justify-center">
        <GenericError
          class="max-w-72 mx-auto text-sm"
          state="danger"
          :reference-code="errorRef"
        >
          {{ $t(`jpc-update-password-error-${errorCode}`) }}
        </GenericError>
      </div>
    </div>
    <LazyUserInterfaceGenericLoader v-if="formLoading" container />
    <DesignSystemModal
      v-model:controller="showSumSub"
      :display-background="false"
      :close-on-backdrop="false"
      :close-on-esc="false"
      :title="$t('password-reset-liveness-check')"
      @error="handleError($event)"
    >
      <template #modal-content>
        <UserInterfaceDynamicFormBuilderSumsub
          :action="ESumSubAction.PasswordReset"
          @complete="confirmPasswordChange"
        />
      </template>
    </DesignSystemModal>
  </div>
</template>
